type InboundSalesforceObjectType = "contact" | "lead";

export type InboundAttribution = {
	readonly channel: "email";
	readonly touchId: string;
	readonly clickId: string;
	readonly recipientId: string;
	readonly salesforceObjectType: InboundSalesforceObjectType;
	readonly brandPageKey?: string;
	readonly utmSource?: string;
	readonly utmMedium?: string;
	readonly utmCampaign?: string;
	readonly utmContent?: string;
	readonly utmTerm?: string;
	readonly attributedAt: string;
	readonly isProbableScanner: boolean;
};

export const INBOUND_TOUCH_COOKIE_NAME = "pbf-inbound-touch";
export const INBOUND_ATTRIBUTION_COOKIE_NAME = "pbf-inbound-attr";
export const INBOUND_ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

export const INBOUND_POSTHOG_PROPERTY_KEYS = [
	"inbound_channel",
	"inbound_touch_id",
	"inbound_click_id",
	"inbound_recipient_id",
	"inbound_salesforce_object_type",
	"inbound_brand_page_key",
	"inbound_utm_source",
	"inbound_utm_medium",
	"inbound_utm_campaign",
	"inbound_utm_content",
	"inbound_utm_term",
	"inbound_is_probable_scanner",
] as const;

function readCookieValue(cookieString: string, name: string) {
	for (const part of cookieString.split(";")) {
		const trimmed = part.trim();
		if (trimmed.startsWith(`${name}=`)) {
			return trimmed.slice(name.length + 1);
		}
	}

	return null;
}

function isInboundSalesforceObjectType(value: string): value is InboundSalesforceObjectType {
	return value === "contact" || value === "lead";
}

function isInboundAttributionRecord(value: object) {
	if (!("channel" in value) || value.channel !== "email") {
		return false;
	}

	if (
		!("touchId" in value) ||
		typeof value.touchId !== "string" ||
		!("clickId" in value) ||
		typeof value.clickId !== "string" ||
		!("recipientId" in value) ||
		typeof value.recipientId !== "string" ||
		!("salesforceObjectType" in value) ||
		typeof value.salesforceObjectType !== "string" ||
		!isInboundSalesforceObjectType(value.salesforceObjectType) ||
		!("attributedAt" in value) ||
		typeof value.attributedAt !== "string" ||
		!("isProbableScanner" in value) ||
		typeof value.isProbableScanner !== "boolean"
	) {
		return false;
	}

	return true;
}

export function serializeInboundAttributionCookieValue(attribution: InboundAttribution) {
	return encodeURIComponent(JSON.stringify(attribution));
}

export function parseInboundAttributionCookieValue(value: string | null) {
	if (value === null) {
		return null;
	}

	try {
		const decoded = decodeURIComponent(value);
		const parsed = JSON.parse(decoded);
		if (typeof parsed !== "object" || parsed === null || !isInboundAttributionRecord(parsed)) {
			return null;
		}

		return parsed;
	} catch {
		return null;
	}
}

export function getInboundAttribution() {
	if (typeof document === "undefined") {
		return null;
	}

	return parseInboundAttributionCookieValue(
		readCookieValue(document.cookie, INBOUND_ATTRIBUTION_COOKIE_NAME),
	);
}

export function getInboundTouchId() {
	if (typeof document === "undefined") {
		return null;
	}

	return readCookieValue(document.cookie, INBOUND_TOUCH_COOKIE_NAME);
}

export function buildInboundPostHogProperties(attribution: InboundAttribution) {
	return {
		inbound_channel: attribution.channel,
		inbound_touch_id: attribution.touchId,
		inbound_click_id: attribution.clickId,
		inbound_recipient_id: attribution.recipientId,
		inbound_salesforce_object_type: attribution.salesforceObjectType,
		...(attribution.brandPageKey !== undefined && {
			inbound_brand_page_key: attribution.brandPageKey,
		}),
		...(attribution.utmSource !== undefined && {
			inbound_utm_source: attribution.utmSource,
		}),
		...(attribution.utmMedium !== undefined && {
			inbound_utm_medium: attribution.utmMedium,
		}),
		...(attribution.utmCampaign !== undefined && {
			inbound_utm_campaign: attribution.utmCampaign,
		}),
		...(attribution.utmContent !== undefined && {
			inbound_utm_content: attribution.utmContent,
		}),
		...(attribution.utmTerm !== undefined && {
			inbound_utm_term: attribution.utmTerm,
		}),
		inbound_is_probable_scanner: attribution.isProbableScanner,
	};
}
